import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import { CloseRounded, SearchOutlined } from "@mui/icons-material";
import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { RootState } from "../../../../core/store";
import { EnrichedServiceReport } from "../../store/serviceReportsSlice";
import { WorkOrder, WorkOrderStatusCode } from "../../models/models";
import serviceReportsApi from "../../api/serviceReportsApi";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

// ─── Constants ─────────────────────────────────────────────
const PANEL_WIDTH = 560;
const TERRACOTTA = "#D4572B";
const TERRACOTTA_HOVER = "#BF4D26";
const TEXT_PRIMARY = "#0C1117";
const TEXT_BODY = "#4A4F57";
const TEXT_MUTED = "#8C9099";
const INPUT_BG = "#F7F5F0";
const BORDER_COLOR = "#E8E5DF";
const FONT_SORA = "'Sora', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";
const SELECTED_BG = "#FDF5F2";
const WO_NUMBER_BLUE = "#3B82F6";

// ─── WO Status config ──────────────────────────────────────

interface WoStatusChipConfig {
  statusId: number;
  label: string;
  filterLabel: string;
  accentColor: string;
  bg: string;
  text: string;
}

const WO_STATUS_CONFIGS: WoStatusChipConfig[] = [
  { statusId: WorkOrderStatusCode.Created, label: "Backlog", filterLabel: "Backlog", accentColor: "#6B7280", bg: "#F4F6F9", text: "#6B7280" },
  { statusId: WorkOrderStatusCode.Assigned, label: "Assigned", filterLabel: "Assigned", accentColor: "#2980B9", bg: "#EBF5FB", text: "#2980B9" },
  { statusId: WorkOrderStatusCode.Scheduled, label: "Scheduled", filterLabel: "Scheduled", accentColor: "#8E44AD", bg: "#F5EEF8", text: "#8E44AD" },
  { statusId: WorkOrderStatusCode.InProgress, label: "In Progress", filterLabel: "In Progress", accentColor: "#F39C12", bg: "#FEF9E7", text: "#F39C12" },
  { statusId: WorkOrderStatusCode.OnHold, label: "On Hold", filterLabel: "On Hold", accentColor: "#C0392B", bg: "#F9EBEA", text: "#C0392B" },
];

function getStatusConfig(statusId: number): WoStatusChipConfig {
  return WO_STATUS_CONFIGS.find((c) => c.statusId === statusId) || WO_STATUS_CONFIGS[0];
}

// ─── Helpers ───────────────────────────────────────────────

function formatSrNumber(num: string | undefined): string {
  if (!num) return "SR-???";
  if (num.startsWith("SR-")) return num;
  const short = num.length > 4 ? num.slice(-4) : num;
  return `SR-${short}`;
}

function timeAgo(dateStr: string | Date | undefined): string {
  if (!dateStr) return "";
  return dayjs(dateStr).fromNow(true) + " ago";
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarColor(name: string): string {
  const colors = ["#D4572B", "#E8A817", "#2D8C4E", "#3B82F6", "#8E44AD", "#C0392B", "#16A085", "#D35400"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

// ─── Props ──────────────────────────────────────────────────

interface LinkToWoPanelProps {
  open: boolean;
  item: EnrichedServiceReport;
  onClose: () => void;
  onLinked: (srNumber: string, woNumber: string) => void;
  translate: (key: string) => string;
}

// ─── Filter types ───────────────────────────────────────────
type MachineFilter = "sameMachine" | "allMachines";

// ─── Component ──────────────────────────────────────────────

const LinkToWoPanel: React.FC<LinkToWoPanelProps> = ({
  open,
  item,
  onClose,
  onLinked,
  translate,
}) => {
  const { sr } = item;

  // All WOs from Redux store
  const workOrders = useAppSelector((s: RootState) => s.serviceReportsPage.workOrders);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [machineFilter, setMachineFilter] = useState<MachineFilter>("sameMachine");
  const [activeStatuses, setActiveStatuses] = useState<Set<number>>(new Set());
  const [selectedWoId, setSelectedWoId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Reset state when panel opens
  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setMachineFilter(sr.machineId ? "sameMachine" : "allMachines");
      setActiveStatuses(new Set());
      setSelectedWoId(null);
      setSubmitting(false);
      setError("");
    }
  }, [open, sr.machineId]);

  // Inject keyframes
  useEffect(() => {
    const id = "link-wo-panel-keyframes";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes linkWoSlideIn {
          0% { transform: translateX(100%); }
          100% { transform: translateX(0); }
        }
        @keyframes linkWoBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Esc key handler
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ─── Filter logic ──────────────────────────────────────────

  // Only open WOs without an existing serviceReportId
  const availableWos = useMemo(() => {
    return workOrders.filter((wo) => {
      // Exclude completed, verified, cancelled
      if (
        wo.workOrderStatusId === WorkOrderStatusCode.Completed ||
        wo.workOrderStatusId === WorkOrderStatusCode.Verified ||
        wo.workOrderStatusId === WorkOrderStatusCode.Cancelled
      ) return false;
      // Multi-SR: WOs with existing SRs are still linkable (removed single-SR filter)
      return true;
    });
  }, [workOrders]);

  const filteredWos = useMemo(() => {
    let wos = availableWos;

    // Machine filter
    if (machineFilter === "sameMachine" && sr.machineId) {
      wos = wos.filter((wo) => wo.machineId === sr.machineId);
    }

    // Status filter
    if (activeStatuses.size > 0) {
      wos = wos.filter((wo) => activeStatuses.has(wo.workOrderStatusId));
    }

    // Search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      wos = wos.filter((wo) =>
        wo.number?.toLowerCase().includes(term) ||
        wo.title?.toLowerCase().includes(term) ||
        wo.machineName?.toLowerCase().includes(term) ||
        wo.machineSerialNumber?.toLowerCase().includes(term)
      );
    }

    // Sort by newest first
    return [...wos].sort((a, b) =>
      dayjs(b.createdDate).valueOf() - dayjs(a.createdDate).valueOf()
    );
  }, [availableWos, machineFilter, sr.machineId, activeStatuses, searchTerm]);

  // Group by machine when "All Machines" active
  const groupedWos = useMemo(() => {
    if (machineFilter === "sameMachine") return null;
    const groups = new Map<string, { label: string; wos: WorkOrder[] }>();
    for (const wo of filteredWos) {
      const key = wo.machineId || "_no_machine";
      if (!groups.has(key)) {
        const machinePart = wo.machineName || translate("serviceReports.unknownMachine");
        const snPart = wo.machineSerialNumber ? ` \u00B7 SN:${wo.machineSerialNumber}` : "";
        groups.set(key, { label: `${machinePart}${snPart}`, wos: [] });
      }
      groups.get(key)!.wos.push(wo);
    }
    return groups;
  }, [filteredWos, machineFilter, translate]);

  // Toggle status filter
  const toggleStatus = useCallback((statusId: number) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(statusId)) next.delete(statusId);
      else next.add(statusId);
      return next;
    });
  }, []);

  // ─── Submit ────────────────────────────────────────────────

  const handleLink = async () => {
    if (!selectedWoId || !sr.id || !sr.number || submitting) return;
    setSubmitting(true);
    setError("");

    try {
      await serviceReportsApi.linkSrToWorkOrder(selectedWoId, {
        serviceReportId: sr.id,
        serviceReportNumber: sr.number,
      });
      const selectedWo = filteredWos.find((wo) => wo.id === selectedWoId);
      onLinked(sr.number, selectedWo?.number || "");
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || translate("serviceReports.actions.linkToWoFailed"));
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const sameMachineLabel = sr.machineId
    ? `${sr.machineModel || translate("serviceReports.unknownMachine")} \u00B7 SN:${sr.machineSerialNumber || sr.machineInternalNumber || "?"}`
    : null;

  return (
    <Box
      sx={{ position: "fixed", inset: 0, zIndex: 1400, display: "flex" }}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(12,17,23,0.18)",
          animation: "linkWoBackdropIn 0.2s ease",
        }}
      />

      {/* Panel */}
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: `${PANEL_WIDTH}px`,
          maxWidth: "100vw",
          backgroundColor: "#FFFFFF",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
          animation: "linkWoSlideIn 0.3s ease",
        }}
      >
        {/* ─── Terracotta accent line ─────────────────────── */}
        <Box sx={{ height: "3px", background: `linear-gradient(90deg, ${TERRACOTTA}, #E8A817)`, flexShrink: 0 }} />

        {/* ─── Header ────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${BORDER_COLOR}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontFamily: FONT_SORA,
                fontSize: "18px",
                fontWeight: 700,
                color: TEXT_PRIMARY,
                mb: "6px",
              }}
            >
              {translate("serviceReports.actions.linkToWoTitle")}
            </Box>
            <Box
              sx={{
                fontFamily: FONT_SORA,
                fontSize: "13px",
                color: TEXT_MUTED,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <Box component="span" sx={{ fontFamily: FONT_MONO, fontWeight: 600, color: WO_NUMBER_BLUE, mr: "6px" }}>
                {formatSrNumber(sr.number)}
              </Box>
              <Box component="span" sx={{ color: TEXT_MUTED }}>
                {"\u00B7 "}
              </Box>
              {sr.title || translate("serviceReports.untitled")}
            </Box>
          </Box>

          {/* Close button */}
          <Box
            onClick={onClose}
            sx={{
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#F2F0EB",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              flexShrink: 0,
              mt: "2px",
              "&:hover": { backgroundColor: "#E8E5DF" },
            }}
          >
            <CloseRounded sx={{ fontSize: 18, color: TEXT_BODY }} />
          </Box>
        </Box>

        {/* ─── Search + Filter Chips ─────────────────────── */}
        <Box sx={{ padding: "16px 24px 12px", flexShrink: 0 }}>
          {/* Search input */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: INPUT_BG,
              borderRadius: "8px",
              padding: "0 12px",
              height: "38px",
              border: "1.5px solid transparent",
              transition: "border-color 0.15s ease",
              "&:focus-within": { borderColor: TERRACOTTA },
              mb: "12px",
            }}
          >
            <SearchOutlined sx={{ fontSize: 16, color: TEXT_MUTED }} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={translate("serviceReports.actions.linkToWoSearchPlaceholder")}
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontFamily: FONT_SORA,
                fontSize: "13px",
                color: TEXT_PRIMARY,
              }}
            />
          </Box>

          {/* Filter chips */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {/* Same Machine / All Machines */}
            <FilterChip
              label={translate("serviceReports.actions.linkToWoSameMachine")}
              active={machineFilter === "sameMachine"}
              onClick={() => setMachineFilter("sameMachine")}
              disabled={!sr.machineId}
            />
            <FilterChip
              label={translate("serviceReports.actions.linkToWoAllMachines")}
              active={machineFilter === "allMachines"}
              onClick={() => setMachineFilter("allMachines")}
            />

            {/* Separator */}
            <Box sx={{ width: "1px", height: "24px", backgroundColor: BORDER_COLOR, alignSelf: "center", mx: "2px" }} />

            {/* Status chips */}
            {WO_STATUS_CONFIGS.map((cfg) => (
              <FilterChip
                key={cfg.statusId}
                label={cfg.filterLabel}
                active={activeStatuses.has(cfg.statusId)}
                onClick={() => toggleStatus(cfg.statusId)}
              />
            ))}
          </Box>
        </Box>

        {/* ─── Same Machine header ───────────────────────── */}
        {machineFilter === "sameMachine" && sameMachineLabel && (
          <Box
            sx={{
              px: "24px",
              pb: "8px",
              fontFamily: FONT_MONO,
              fontSize: "11px",
              fontWeight: 600,
              color: TEXT_MUTED,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {translate("serviceReports.actions.linkToWoSameMachineHeader")} — {sameMachineLabel}
          </Box>
        )}

        {/* ─── WO List (scrollable) ──────────────────────── */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: "24px",
            pb: "8px",
            scrollbarWidth: "thin",
            scrollbarColor: "#C1C7CD transparent",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#C1C7CD", borderRadius: "3px" },
          }}
        >
          {filteredWos.length === 0 ? (
            /* Empty state */
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: "48px",
                gap: "8px",
              }}
            >
              <Box sx={{ fontFamily: FONT_SORA, fontSize: "14px", fontWeight: 600, color: TEXT_BODY }}>
                {translate("serviceReports.actions.linkToWoNoResults")}
              </Box>
              <Box sx={{ fontFamily: FONT_SORA, fontSize: "12px", color: TEXT_MUTED, textAlign: "center" }}>
                {translate("serviceReports.actions.linkToWoNoResultsHint")}
              </Box>
            </Box>
          ) : machineFilter === "allMachines" && groupedWos ? (
            /* Grouped by machine */
            Array.from(groupedWos.entries()).map(([machineKey, group]) => (
              <Box key={machineKey} sx={{ mb: "16px" }}>
                <Box
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: "11px",
                    fontWeight: 600,
                    color: TEXT_MUTED,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    mb: "8px",
                    pt: "4px",
                  }}
                >
                  {group.label}
                </Box>
                {group.wos.map((wo) => (
                  <WoRow
                    key={wo.id}
                    wo={wo}
                    isSelected={selectedWoId === wo.id}
                    onSelect={() => setSelectedWoId(wo.id)}
                    showMachine={false}
                    translate={translate}
                  />
                ))}
              </Box>
            ))
          ) : (
            /* Flat list (Same Machine) */
            filteredWos.map((wo) => (
              <WoRow
                key={wo.id}
                wo={wo}
                isSelected={selectedWoId === wo.id}
                onSelect={() => setSelectedWoId(wo.id)}
                showMachine={true}
                translate={translate}
              />
            ))
          )}
        </Box>

        {/* Error */}
        {error && (
          <Box sx={{ fontFamily: FONT_SORA, fontSize: "12px", color: "#DC2626", px: "24px", pb: "8px" }}>
            {error}
          </Box>
        )}

        {/* ─── Footer ────────────────────────────────────── */}
        <Box
          sx={{
            borderTop: `1px solid ${BORDER_COLOR}`,
            padding: "14px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {/* Count */}
          <Box sx={{ fontFamily: FONT_MONO, fontSize: "11px", color: TEXT_MUTED }}>
            {translate("serviceReports.actions.linkToWoShowing")
              .replace("{{filtered}}", String(filteredWos.length))
              .replace("{{total}}", String(availableWos.length))}
          </Box>

          {/* Buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              onClick={onClose}
              sx={{
                fontFamily: FONT_SORA,
                fontSize: "13px",
                fontWeight: 500,
                color: TEXT_MUTED,
                cursor: "pointer",
                padding: "10px 16px",
                borderRadius: "8px",
                transition: "all 0.15s ease",
                "&:hover": { backgroundColor: "#F2F0EB", color: TEXT_BODY },
              }}
            >
              {translate("serviceReports.actions.cancel")}
            </Box>
            <Box
              onClick={handleLink}
              sx={{
                fontFamily: FONT_SORA,
                fontSize: "13px",
                fontWeight: 600,
                color: "#FFFFFF",
                backgroundColor: selectedWoId && !submitting ? TERRACOTTA : "#C1BDB7",
                padding: "10px 24px",
                borderRadius: "24px",
                cursor: selectedWoId && !submitting ? "pointer" : "default",
                transition: "all 0.15s ease",
                "&:hover": selectedWoId && !submitting
                  ? {
                      backgroundColor: TERRACOTTA_HOVER,
                      transform: "translateY(-1px)",
                      boxShadow: `0 4px 12px rgba(212,87,43,0.25)`,
                    }
                  : {},
              }}
            >
              {submitting
                ? translate("serviceReports.actions.linkToWoLinking")
                : translate("serviceReports.actions.linkToWoButton")}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Filter Chip sub-component ──────────────────────────────

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const FilterChip: React.FC<FilterChipProps> = ({ label, active, onClick, disabled }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      fontFamily: FONT_SORA,
      fontSize: "12px",
      fontWeight: active ? 600 : 500,
      color: disabled ? "#C1BDB7" : active ? "#FFFFFF" : TEXT_BODY,
      backgroundColor: disabled ? "#F7F5F0" : active ? TEXT_PRIMARY : "#FFFFFF",
      border: active ? "none" : `1px solid ${BORDER_COLOR}`,
      borderRadius: "20px",
      padding: active ? "5px 14px" : "4px 13px",
      cursor: disabled ? "default" : "pointer",
      userSelect: "none",
      transition: "all 0.15s ease",
      whiteSpace: "nowrap",
      opacity: disabled ? 0.5 : 1,
      "&:hover": disabled
        ? {}
        : {
            backgroundColor: active ? "#1a1a2e" : "#F7F5F0",
            borderColor: active ? "transparent" : "#D1CCC4",
          },
    }}
  >
    {label}
  </Box>
);

// ─── WO Row sub-component ───────────────────────────────────

interface WoRowProps {
  wo: WorkOrder;
  isSelected: boolean;
  onSelect: () => void;
  showMachine: boolean;
  translate: (key: string) => string;
}

const WoRow: React.FC<WoRowProps> = ({ wo, isSelected, onSelect, showMachine, translate }) => {
  const statusCfg = getStatusConfig(wo.workOrderStatusId);

  return (
    <Box
      onClick={onSelect}
      sx={{
        display: "flex",
        gap: "12px",
        padding: "12px 14px",
        borderRadius: "10px",
        cursor: "pointer",
        backgroundColor: isSelected ? SELECTED_BG : "transparent",
        borderLeft: isSelected ? `3px solid ${TERRACOTTA}` : "3px solid transparent",
        transition: "all 0.15s ease",
        mb: "4px",
        "&:hover": {
          backgroundColor: isSelected ? SELECTED_BG : "#FAFAF8",
        },
      }}
    >
      {/* Radio circle */}
      <Box
        sx={{
          width: "18px",
          height: "18px",
          borderRadius: "50%",
          border: isSelected ? `5px solid ${TERRACOTTA}` : `2px solid #D1CCC4`,
          backgroundColor: isSelected ? "#FFFFFF" : "transparent",
          flexShrink: 0,
          mt: "2px",
          transition: "all 0.15s ease",
        }}
      />

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Top line: WO number + status badge */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "4px" }}>
          <Box
            sx={{
              fontFamily: FONT_MONO,
              fontSize: "12px",
              fontWeight: 600,
              color: WO_NUMBER_BLUE,
            }}
          >
            {wo.number}
          </Box>
          {/* Status badge with left-bar accent */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: "3px",
                height: "16px",
                backgroundColor: statusCfg.accentColor,
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                fontFamily: FONT_SORA,
                fontSize: "10px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                color: statusCfg.text,
                backgroundColor: statusCfg.bg,
                padding: "2px 8px",
              }}
            >
              {statusCfg.label}
            </Box>
          </Box>
        </Box>

        {/* Title */}
        <Box
          sx={{
            fontFamily: FONT_SORA,
            fontSize: "14px",
            fontWeight: 600,
            color: TEXT_PRIMARY,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mb: "4px",
          }}
        >
          {wo.title || translate("serviceReports.untitled")}
        </Box>

        {/* Meta line: machine + assignee + age */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexWrap: "wrap",
          }}
        >
          {/* Machine info */}
          {showMachine && wo.machineName && (
            <>
              <Box
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: "11px",
                  color: TEXT_MUTED,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "180px",
                }}
              >
                {wo.machineName}
                {wo.machineSerialNumber && ` \u00B7 SN:${wo.machineSerialNumber}`}
              </Box>
              <Box sx={{ color: TEXT_MUTED, fontSize: "11px" }}>{"\u00B7"}</Box>
            </>
          )}

          {/* Location */}
          {wo.locationName && (
            <>
              <Box sx={{ fontFamily: FONT_SORA, fontSize: "11px", color: TEXT_MUTED, whiteSpace: "nowrap" }}>
                {wo.locationName}
              </Box>
              <Box sx={{ color: TEXT_MUTED, fontSize: "11px" }}>{"\u00B7"}</Box>
            </>
          )}

          {/* Assignee */}
          {wo.assignedToUserName && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Box
                  sx={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: avatarColor(wo.assignedToUserName),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "8px",
                    fontWeight: 700,
                    color: "#FFFFFF",
                    fontFamily: FONT_SORA,
                    flexShrink: 0,
                  }}
                >
                  {getInitials(wo.assignedToUserName)}
                </Box>
                <Box sx={{ fontFamily: FONT_SORA, fontSize: "11px", color: TEXT_MUTED, whiteSpace: "nowrap" }}>
                  {wo.assignedToUserName}
                </Box>
              </Box>
              <Box sx={{ color: TEXT_MUTED, fontSize: "11px" }}>{"\u00B7"}</Box>
            </>
          )}

          {/* Age */}
          <Box sx={{ fontFamily: FONT_MONO, fontSize: "11px", color: TEXT_MUTED, whiteSpace: "nowrap" }}>
            {timeAgo(wo.createdDate)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LinkToWoPanel;
